<template>
	<div class="checkout-form">

		<div class="checkout-form__wrapper insicht insicht--fade-in insicht--fast">

			<div class="checkout-form__container">

				<h1 class="checkout-form__headline" v-html="$t('checkout.title')"/>

				<Button
					@click.prevent="backToShop"
					class="checkout-form__back-to-shop checkout-form__back-to-shop--top"
					level="tertiary"
					type="button"
					:text="$t('checkout.continueShopping')"
					arrow
					reverse
					:disabled="formLocked"
				/>

				<div
					class="checkout-form__block checkout-form__block--postdelivery"
					v-if="showPostdeliveryHint"
				>
					<div class="checkout-form__login">
						<div class="checkout-form__login-content">
							<h2
								class="checkout-form__login-title"
								v-html="$t('checkout.postdelivery.title')"
							/>
						</div>

						<i18n
							path="checkout.login.actions"
							tag="div"
							class="checkout-form__login-actions"
						>
							<template #login>
								<Button
									@click.prevent="openLogin"
									class="checkout-form__login-action"
									level="tertiary"
									type="button"
									:text="$t('checkout.login.loginButton')"
								/>
							</template>
							<template #register>
								<Button
									@click.prevent="openRegister"
									class="checkout-form__login-action"
									level="tertiary"
									type="button"
									:text="$t('checkout.login.registerButton')"
								/>
							</template>
						</i18n>
					</div>
				</div>

				<div
					class="checkout-form__block"
					v-if="!isLoggedIn"
				>
					<div class="checkout-form__login">
						<SVGUser
							class="checkout-form__login-icon"
						/>
						<div class="checkout-form__login-content">
							<h2
								class="checkout-form__login-title"
								v-html="$t('checkout.login.title')"
							/>
							<div
								class="checkout-form__login-text"
								v-html="$t('checkout.login.text')"
							/>
						</div>

						<i18n
							path="checkout.login.actions"
							tag="div"
							class="checkout-form__login-actions"
						>
							<template #login>
								<Button
									@click.prevent="openLogin"
									class="checkout-form__login-action"
									level="tertiary"
									type="button"
									:text="$t('checkout.login.loginButton')"
								/>
							</template>
							<template #register>
								<Button
									@click.prevent="openRegister"
									class="checkout-form__login-action"
									level="tertiary"
									type="button"
									:text="$t('checkout.login.registerButton')"
								/>
							</template>
						</i18n>
					</div>

					<Button
						@click.prevent="scrollToForm"
						class="checkout-form__login-guest"
						level="tertiary"
						type="button"
						:text="$t('checkout.login.continueAsGuest')"
						arrow
						:disabled="formLocked"
					/>
				</div>

				<GeneralMessage
					v-for="(message, i) in notifyMessages"
					ref="notificationMessages"
					:key="'message'+i"
					:message="message.message"
					:type="message.type"
				/>

				<CheckoutCartButton
					sticky
				/>

				<template v-if="!addressSet">
					<ValidationObserver
						ref="shippingAddressForm"
						v-slot="{ invalid, errors }"
						tag="form"
						class="checkout-form__form-wrapper"
						@submit.prevent="onShippingAddress"
						novalidate
					>
						<div class="checkout-form__block">
							<h2 class="checkout-form__block-title" v-html="addressTitle"/>

							<div class="form-row">
								<ValidationProvider
									class="form-row__wrapper"
									ref="email"
									name="email"
									rules="required|email"
									v-slot="{ errors, invalid, validated }"
									tag="div"
									:customMessages="{ required: $t('forms.errors.fieldEmpty'), email: $t('forms.errors.emailInvalid') }"
								>
									<FormInput
										type="email"
										:placeholder="$t('forms.labels.email')"
										:label="$t('forms.labels.email')"
										name="email"
										v-model="form.email"
										:error="errors.length > 0"
										:readonly="!!isLoggedIn"
										:error-message="errors[0]"
										:hint="$t('checkout.emailHint')"
										:disabled="formLocked"
										required
									/>
								</ValidationProvider>
							</div>

							<ValidationObserver
								tag="section"
								ref="shippingAddress"
								vid="shippingAddress"
								v-slot="{ invalid }"
							>
								<!-- <div class="form-row">
									<FormInputSelect
										:options="genders"
										name="gender"
										v-model="form.shippingAddress.gender"
										:label="$t('forms.labels.gender')"
										:labelOption="$t('forms.labels.optional')"
									/>
								</div> -->
								<div class="form-row">
									<ValidationProvider
										class="form-row__wrapper"
										ref="shippingFirstname"
										name="shippingFirstname"
										rules="required"
										v-slot="{ errors, invalid, validated }"
										tag="div"
										:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
									>
										<FormInput
											type="text"
											:placeholder="$t('forms.labels.firstname')"
											:label="$t('forms.labels.firstname')"
											name="firstname"
											v-model="form.shippingAddress.firstName"
											:error="errors.length > 0"
											:error-message="errors[0]"
											:disabled="formLocked"
											required
										/>
									</ValidationProvider>
									<ValidationProvider
										class="form-row__wrapper"
										ref="shippingLastname"
										name="shippingLastname"
										rules="required"
										v-slot="{ errors, invalid, validated }"
										tag="div"
										:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
									>
										<FormInput
											type="text"
											:placeholder="$t('forms.labels.lastname')"
											:label="$t('forms.labels.lastname')"
											name="lastname"
											v-model="form.shippingAddress.lastName"
											:error="errors.length > 0"
											:error-message="errors[0]"
											:disabled="formLocked"
											required
										/>
									</ValidationProvider>
								</div>

								<div class="form-row">
									<FormInput
										type="text"
										:placeholder="$t('forms.labels.company')"
										:label="$t('forms.labels.company')"
										:labelOption="$t('forms.labels.optional')"
										name="company"
										v-model="form.shippingAddress.company"
										:required="false"
										:disabled="formLocked"
									/>
								</div>

								<div class="form-row">
									<ValidationObserver
										class="form-row__wrapper"
										tag="div"
										ref="shippingStreetNr"
										v-slot="{ validated, invalid, errors }"
									>
										<div class="form-group">
											<ValidationProvider
												v-if="!themeValue('THEME_REVERSE_STREET_NUMBER')"
												class="form-group__span--9"
												ref="shippingStreet"
												name="shippingStreet"
												:rules="isDelivery ? 'required' : ''"
												v-slot="{ errors, invalid }"
												tag="div"
												:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
											>
												<FormInputAutocomplete
													v-if="hasFeature('FEATURE_ADDRESS_AUTOCOMPLETE')"
													type="text"
													:placeholder="$t('forms.labels.street')"
													:label="$t('forms.labels.street')"
													:labelOption="isDelivery ? '' : $t('forms.labels.optional')"
													name="street"
													v-model="form.shippingAddress.street"
													:suggestions="addressSuggestions"
													:error="errors.length > 0"
													:attributes="{
														'data-1p-ignore': true
													}"
													icon="search"
													:disabled="formLocked"
													:required="isDelivery"
													@keydown="onAddressInput"
													@suggestion:selected="onAddressSuggestion"
												/>
												<FormInput
													v-else
													type="text"
													:placeholder="$t('forms.labels.street')"
													:label="$t('forms.labels.street')"
													:labelOption="isDelivery ? '' : $t('forms.labels.optional')"
													name="street"
													v-model="form.shippingAddress.street"
													:error="errors.length > 0"
													:disabled="formLocked"
													:required="isDelivery"
												/>
											</ValidationProvider>
											<ValidationProvider
												class="form-group__span--3"
												ref="shippingStreetNumber"
												name="shippingStreetNumber"
												:rules="isDelivery ? 'required' : ''"
												v-slot="{ errors, invalid }"
												tag="div"
												:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
											>
												<FormInput
													type="text"
													:placeholder="$t('forms.labels.number')"
													:label="$t('forms.labels.number')"
													:labelOption="isDelivery ? '' : $t('forms.labels.optional')"
													name="number"
													v-model="form.shippingAddress.streetNumber"
													:error="errors.length > 0"
													:disabled="formLocked"
													:required="isDelivery"
												/>
											</ValidationProvider>
											<ValidationProvider
												v-if="themeValue('THEME_REVERSE_STREET_NUMBER')"
												class="form-group__span--9"
												ref="shippingStreet"
												name="shippingStreet"
												:rules="isDelivery ? 'required' : ''"
												v-slot="{ errors, invalid }"
												tag="div"
												:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
											>
												<FormInput
													type="text"
													:placeholder="$t('forms.labels.street')"
													:label="$t('forms.labels.street')"
													:labelOption="isDelivery ? '' : $t('forms.labels.optional')"
													name="street"
													v-model="form.shippingAddress.street"
													:error="errors.length > 0"
													:disabled="formLocked"
													:required="isDelivery"
												/>
											</ValidationProvider>
										</div>
										<div
											class="form-input__error | form-input-error form-group__span--12"
											v-if="invalid && validated"
											v-html="joinErrorMessage(errors)"
										/>
									</ValidationObserver>

									<ValidationObserver
										class="form-row__wrapper"
										tag="div"
										ref="shippingZipCity"
										v-slot="{ dirty, validated, invalid, errors }"
									>
										<div class="form-group">
											<ValidationProvider
												class="form-group__span--4 form-group__span--xxl-3"
												ref="shippingZip"
												name="shippingZip"
												:rules="isDelivery ? 'required|zip' : 'zip'"
												v-slot="{ errors, invalid }"
												tag="div"
												:customMessages="{ required: $t('forms.errors.fieldEmpty'), zip: $t('forms.errors.zip') }"
											>
												<FormInputZip
													v-model="form.shippingAddress.postcode"
													:placeholder="$t('forms.labels.zip')"
													:label="$t('forms.labels.zip')"
													:labelOption="isDelivery ? '' : $t('forms.labels.optional')"
													name="zip"
													:error="errors.length > 0"
													:required="isDelivery"
													:disabled="formLocked"
												/>
											</ValidationProvider>
											<ValidationProvider
												class="form-group__span--8 form-group__span--xxl-9"
												ref="shippingCity"
												name="shippingCity"
												:rules="isDelivery ? 'required' : ''"
												v-slot="{ errors, invalid }"
												tag="div"
												:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
											>
												<FormInput
													type="text"
													:placeholder="$t('forms.labels.city')"
													:label="$t('forms.labels.city')"
													:labelOption="isDelivery ? '' : $t('forms.labels.optional')"
													name="city"
													v-model="form.shippingAddress.city"
													:error="errors.length > 0"
													:required="isDelivery"
													:disabled="formLocked"
												/>
											</ValidationProvider>
										</div>
										<div
											class="form-input__error | form-input-error form-group__span--12"
											v-if="invalid && (validated || dirty)"
											v-html="Object.values(errors).filter(value => !!value.join('').trim()).join(' ')"
										/>
									</ValidationObserver>

								</div>
								<div class="form-row">
									<div class="form-row__wrapper">
										<FormInput
											type="text"
											:placeholder="$t('forms.labels.addressDetails')"
											:label="$t('forms.labels.addressDetails')"
											:labelOption="$t('forms.labels.optional')"
											name="addressdetails"
											v-model="form.shippingAddress.details"
											:required="false"
											:hint="$t('forms.labels.courierHint')"
											:disabled="formLocked"
										/>
									</div>
									<ValidationProvider
										class="form-row__wrapper"
										ref="shippingCountry"
										name="shippingCountry"
										:rules="isDelivery ? 'required' : ''"
										v-slot="{ errors, invalid }"
										tag="div"
										:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
									>
										<FormInputSelect
											:options="countryOptions"
											v-model="form.shippingAddress.countryCode"
											:label="$t('forms.labels.country')"
											name="country"
											:disabled="formLocked"
											:required="isDelivery"
										/>
									</ValidationProvider>
								</div>

								<div class="form-row">
									<ValidationProvider
										class="form-row__wrapper"
										ref="shippingPhone"
										name="shippingPhone"
										rules="required|phone_int"
										v-slot="{ errors, invalid, validated }"
										tag="div"
										:customMessages="{ required: $t('forms.errors.fieldEmpty'), phone_int: $t('forms.errors.phone') }"
									>
										<FormInput
											type="tel"
											:placeholder="$t('forms.labels.phone')"
											:label="$t('forms.labels.phone')"
											name="phone"
											v-model="form.shippingAddress.phoneNumber"
											:error="errors.length > 0"
											:error-message="errors[0]"
											:hint="$t('checkout.phoneHint')"
											:disabled="formLocked"
											required
										/>
									</ValidationProvider>
								</div>

							</ValidationObserver>

						</div>

						<Button
							:text="$t('checkout.continue')"
							level="primary"
							type="submit"
						/>
					</ValidationObserver>
				</template>

				<template v-else>
					<div class="checkout-form__block">
						<h2 class="checkout-form__block-title" v-html="addressTitle"/>
						<address class="checkout-form__block-text" v-html="formattedShippingAddress"/>
						<Button
							level="tertiary"
							type="button"
							:text="$t('checkout.changeShippingAddress')"
							arrow
							:disabled="formLocked"
							@click.prevent="onShippingAddressEdit"
						/>
					</div>

					<template v-if="!deliverySet">
						<ValidationObserver
							ref="deliveryTimeForm"
							v-slot="{ invalid, errors }"
							tag="form"
							class="checkout-form__form-wrapper"
							@submit.prevent="onDeliveryTime()"
							novalidate
						>
							<div
								class="checkout-form__block"
								v-if="isDelivery"
							>
								<h2 class="checkout-form__block-title" v-html="$t('checkout.deliveryInfoTitle')"/>
								<div class="form-row">
									<div class="checkout-form__delivery-info" v-html="$t('checkout.deliveryInfoText')"/>
								</div>
							</div>

							<div
								class="checkout-form__block"
							>
								<template
									v-if="deliveryMethods.length > 1"
								>
									<h2 class="checkout-form__block-title" v-html="$t('checkout.delivery.methodTitle')"/>
									<div class="form-row">
										<ValidationProvider
											ref="deliveryMethod"
											name="deliveryMethod"
											rules="required"
											v-slot="{ errors, invalid }"
											tag="div"
											:customMessages="{ required: $t('checkout.delivery.methodError') }"
										>
											<FormInputRadio
												v-model="deliveryMethod"
												name="deliveryMethod"
												:options="deliveryMethods"
												:disabled="formLocked"
												:error="errors.length > 0"
												:error-message="errors[0]"
												vertical
												required
											/>
										</ValidationProvider>
									</div>
								</template>

								<template
									v-if="(deliveryMethods.length && deliveryMethod) || !deliveryMethods.length"
								>
									<h2 class="checkout-form__block-title" v-html="timeTitle"/>
									<div class="form-row">
										<div class="form-row__wrapper">
											<ValidationProvider
												ref="deliveryDate"
												name="deliveryDate"
												rules="required"
												v-slot="{ errors, invalid }"
												tag="div"
												:customMessages="{ required: $t('checkout.errorDeliveryDate') }"
											>
												<FormInputDate
													:placeholder="$t('forms.labels.date')"
													:label="$t('forms.labels.date')"
													v-model="selectedDate"
													:initialValue="selectedDate"
													:min-date="deliveryMinDate"
													:max-date="deliveryMaxDate"
													:isDateDisabled="isDateDisabled"
													:disabled="formLocked"
													:error="errors.length > 0"
													:error-message="errors[0]"
													required
													show-day
												/>
											</ValidationProvider>
											<ValidationProvider
												ref="deliveryTime"
												name="deliveryTime"
												rules="required"
												v-slot="{ errors, invalid }"
												tag="div"
												:customMessages="{ required: $t('checkout.errorDeliveryTime') }"
											>
												<TimePicker
													v-model="selectedTime"
													:times="deliveryTimes"
													:label="$t('forms.labels.time')"
													:disabled="formLocked"
													:error="errors.length > 0"
													:error-message="errors[0]"
													single-col
													required
												/>
											</ValidationProvider>
										</div>
									</div>

									<Button
										:text="$t('checkout.continue')"
										level="primary"
										type="submit"
									/>
								</template>
							</div>
						</ValidationObserver>
					</template>

					<template v-else>
						<div
							class="checkout-form__block"
							v-if="isDelivery"
						>
							<h2 class="checkout-form__block-title" v-html="$t('checkout.deliveryInfoTitle')"/>
							<div class="form-row">
								<div class="checkout-form__delivery-info" v-html="$t('checkout.deliveryInfoText')"/>
							</div>
						</div>

						<div class="checkout-form__block">
							<h2 class="checkout-form__block-title" v-html="timeTitle"/>
							<div class="checkout-form__time">
								<SVGClock
									class="checkout-form__time-icon"
								/>
								<span
									class="checkout-form__time-text"
									v-html="deliveryTimeEnhanced"
								/>
							</div>
							<Button
								class="checkout-form__time-edit"
								type="button"
								@click.prevent="onDeliveryTimeEdit"
								level="tertiary"
								arrow
								:text="timeEditLabel"
								:disabled="formLocked"
							/>
						</div>

						<ValidationObserver
							ref="form"
							v-slot="{ invalid, errors }"
							tag="form"
							class="checkout-form__form-wrapper"
							@submit.prevent="onDeliveryTime"
							novalidate
						>


							<div class="checkout-form__block">
								<h2 class="checkout-form__block-title" v-html="$t('checkout.chopsticksTitle')"/>
								<FormInputCheckbox
										name="chopsticks"
										value="chopsticks"
										v-model="form.chopsticks"
										immediate
										:disabled="formLocked"
									>
										<span v-html="$t('checkout.chopsticksLabel')"/>
									</FormInputCheckbox>
							</div>

							<div class="checkout-form__block">
								<h2 class="checkout-form__block-title" v-html="$t('checkout.notesTitle')"/>
								<div class="form-row">
									<FormInputTextarea
										:placeholder="$t('forms.labels.notes')"
										:label="$t('forms.labels.notes')"
										v-model="form.notes"
										:disabled="formLocked"
									/>
								</div>
							</div>

							<div
								class="checkout-form__block"
								v-if="checkoutPayments.length"
							>
								<h2 class="checkout-form__block-title" v-html="$t('checkout.paymentTitle')"/>

								<div class="form-row">
									<ValidationProvider
										class="form-row__wrapper"
										v-if="checkoutPayments.length"
										rules="required"
										name="paymentCode"
										tag="div"
										v-slot="{ invalid, errors, validated }"
										:customMessages="{ required: $t('checkout.errorPaymentMethod') }"
									>
										<FormInputPayment
											v-model="form.paymentCode"
											:methods="checkoutPayments"
											@input="onPaymentChanged"
											:error="errors.length > 0"
											:error-message="errors[0]"
											:disabled="formLocked"
										/>
									</ValidationProvider>
								</div>
							</div>

							<div
								class="checkout-form__block"
								v-if="showBirthday"
							>
								<div class="form-row">
									<ValidationProvider
										name="birthday"
										rules="required"
										v-slot="{ errors, invalid, validated }"
										tag="div"
										:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
									>
										<h2 class="checkout-form__block-subtitle" v-html="$t('checkout.birthdayTitle')"/>
										<div class="checkout-form__block-text" v-html="$t('checkout.birthdayText')"/>
										<FormInputDate
											:placeholder="$t('forms.labels.birthday')"
											:label="$t('forms.labels.birthday')"
											v-model="form.birthday"
											max-date="today"
											:error="errors.length > 0"
											:error-message="errors[0]"
											:disabled="formLocked"
											required
										/>
									</ValidationProvider>
								</div>
							</div>

							<div
								class="checkout-form__block"
								v-if="showBillingAddress"
							>
								<h2 class="checkout-form__block-title" v-html="'Rechnungsadresse'"/>

								<FormInputCheckbox
									name="billingAddressIdentical"
									value="billingAddressIdentical"
									v-model="form.billingAddressIdentical"
									:disabled="formLocked"
									immediate
								>
									<span v-html="'Rechnungsadresse ist gleich wie Lieferadresse'"/>
								</FormInputCheckbox>

								<ValidationObserver
									tag="section"
									ref="billingAddress"
									vid="billingAddress"
									v-slot="{ invalid }"
									v-if="!form.billingAddressIdentical"
								>
									<div class="form-row">
										<FormInputSelect
											:options="genders"
											name="gender"
											v-model="form.billingAddress.gender"
											:label="$t('forms.labels.gender')"
											:labelOption="$t('forms.labels.optional')"
											:disabled="formLocked"
										/>
									</div>
									<div class="form-row">
										<ValidationProvider
											class="form-row__wrapper"
											name="billingFirstname"
											rules="required"
											v-slot="{ errors, invalid, validated }"
											tag="div"
											:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
										>
											<FormInput
												type="text"
												:placeholder="$t('forms.labels.firstname')"
												:label="$t('forms.labels.firstname')"
												name="firstname"
												v-model="form.billingAddress.firstName"
												:error="errors.length > 0"
												:error-message="errors[0]"
												:disabled="formLocked"
												required
											/>
										</ValidationProvider>
										<ValidationProvider
											class="form-row__wrapper"
											name="billingLastname"
											rules="required"
											v-slot="{ errors, invalid, validated }"
											tag="div"
											:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
										>
											<FormInput
												type="text"
												:placeholder="$t('forms.labels.lastname')"
												:label="$t('forms.labels.lastname')"
												name="lastname"
												v-model="form.billingAddress.lastName"
												:error="errors.length > 0"
												:error-message="errors[0]"
												:disabled="formLocked"
												required
											/>
										</ValidationProvider>
									</div>

									<div class="form-row">
										<FormInput
											type="text"
											:placeholder="$t('forms.labels.company')"
											:label="$t('forms.labels.company')"
											:labelOption="$t('forms.labels.optional')"
											name="company"
											v-model="form.billingAddress.company"
											:required="false"
											:disabled="formLocked"
										/>
									</div>

									<div class="form-row">
										<ValidationObserver
											class="form-row__wrapper"
											tag="div"
											ref="billingStreetNr"
											v-slot="{ validated, invalid, errors }"
										>
											<div class="form-group">
												<ValidationProvider
													class="form-group__span--9"
													name="billingStreet"
													rules="required"
													v-slot="{ errors, invalid }"
													tag="div"
													:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
												>
													<FormInput
														type="text"
														:placeholder="$t('forms.labels.street')"
														:label="$t('forms.labels.street')"
														name="street"
														v-model="form.billingAddress.street"
														:error="errors.length > 0"
														:disabled="formLocked"
														required
													/>
												</ValidationProvider>
												<ValidationProvider
														class="form-group__span--3"
														name="billingStreetNumber"
														rules="required"
														v-slot="{ errors, invalid }"
														tag="div"
														:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
													>
													<FormInput
														type="text"
														:placeholder="$t('forms.labels.number')"
														:label="$t('forms.labels.number')"
														name="number"
														v-model="form.billingAddress.streetNumber"
														:error="errors.length > 0"
														:disabled="formLocked"
														required
													/>
												</ValidationProvider>
											</div>
											<div
												class="form-input__error | form-input-error form-group__span--12"
												v-if="invalid && validated"
												v-html="Object.values(errors).filter(value => !!value.join('').trim()).join(' ')"
											/>
										</ValidationObserver>

										<ValidationObserver
											class="form-row__wrapper"
											tag="div"
											ref="billingZipCity"
											v-slot="{ dirty, validated, invalid, errors }"
										>
											<div class="form-group">
												<ValidationProvider
													class="form-group__span--4 form-group__span--xxl-3"
													name="billingZip"
													rules="required|zip"
													v-slot="{ errors, invalid }"
													tag="div"
													:customMessages="{ required: $t('forms.errors.fieldEmpty'), zip: $t('forms.errors.zip') }"
												>
													<FormInputZip
														v-model="form.billingAddress.postcode"
														:placeholder="$t('forms.labels.zip')"
														:label="$t('forms.labels.zip')"
														name="zip"
														:error="errors.length > 0"
														:required="isDelivery"
														:disabled="formLocked"
													/>
												</ValidationProvider>
												<ValidationProvider
													class="form-group__span--8 form-group__span--xxl-9"
													name="billingCity"
													rules="required"
													v-slot="{ errors, invalid }"
													tag="div"
													:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
												>
													<FormInput
														type="text"
														:placeholder="$t('forms.labels.city')"
														:label="$t('forms.labels.city')"
														name="city"
														v-model="form.billingAddress.city"
														:error="errors.length > 0"
														required
														:disabled="formLocked"
													/>
												</ValidationProvider>
											</div>
											<div
												class="form-input__error | form-input-error form-group__span--12"
												v-if="invalid && (validated || dirty)"
												v-html="Object.values(errors).filter(value => !!value.join('').trim()).join(' ')"
											/>
										</ValidationObserver>

									</div>
									<div class="form-row">
										<div class="form-row__wrapper">
											<FormInput
												type="text"
												:placeholder="$t('forms.labels.addressDetails')"
												:label="$t('forms.labels.addressDetails')"
												:labelOption="$t('forms.labels.optional')"
												name="addressdetails"
												v-model="form.billingAddress.details"
												:required="false"
												:hint="$t('forms.labels.courierHint')"
												:disabled="formLocked"
											/>
										</div>
										<ValidationProvider
											class="form-row__wrapper"
											name="billingCountry"
											rules="required"
											v-slot="{ errors, invalid }"
											tag="div"
											:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
										>
											<FormInputSelect
												:options="countryOptions"
												v-model="form.billingAddress.countryCode"
												:label="$t('forms.labels.country')"
												name="country"
												:disabled="formLocked"
											/>
										</ValidationProvider>
									</div>

									<div class="form-row">
										<ValidationProvider
											class="form-row__wrapper"
											name="billingPhone"
											rules="required|phone_int"
											v-slot="{ errors, invalid, validated }"
											tag="div"
											:customMessages="{ required: $t('forms.errors.fieldEmpty'), phone_int: $t('forms.errors.phone') }"
										>
											<FormInput
												type="tel"
												:placeholder="$t('forms.labels.phone')"
												:label="$t('forms.labels.phone')"
												name="phone"
												v-model="form.billingAddress.phoneNumber"
												:error="errors.length > 0"
												:error-message="errors[0]"
												:hint="$t('checkout.phoneHint')"
												:disabled="formLocked"
												required
											/>
										</ValidationProvider>
									</div>

								</ValidationObserver>
							</div>

							<div
								v-if="showTipInput"
							>
								<h2 class="checkout-form__block-title" v-html="$t('checkout.tipTitle')"/>

								<div class="form-row">
									<div class="form-row__wrapper">
										<FormInputRange
											:min="0"
											:max="5000"
											:step="50"
											:displayValue="$options.filters.madpricecent(tip, 'short')"
											v-model.number="tip"
											:prefix="themeValue('THEME_CURRENCY_PREFIX')"
											:disabled="formLocked"
										/>
									</div>
								</div>
							</div>

							<div class="checkout-form__block">
								<h2 class="checkout-form__block-title" v-html="$t('checkout.couponTitle')"/>

								<div class="form-row">
									<div class="form-row__wrapper">
										<ValidationProvider
											:rules="{regex:/.{3,40}/}"
											ref="couponCode"
											name="couponCode"
											tag="div"
											v-slot="{ invalid, errors, validated }"
											:customMessages="{ regex: $t('forms.errors.format') }"
										>
											<div class="form-combo form-combo--input-button">
												<FormInput
													v-model="couponCode"
													:label="$t('checkout.couponLabel')"
													:placeholder="$t('checkout.couponLabel')"
													@keydown.enter.prevent="addCoupon"
													:disabled="formLocked"
												/>
												<Button
													@click.prevent="addCoupon"
													type="button"
													level="secondary"
													:text="$t('checkout.couponButton')"
													:variants="[
														'small'
													]"
												/>
											</div>
											<span
												class="form-input__hint | form-input-hint"
												v-if="$te('checkout.couponHint')"
												v-html="$t('checkout.couponHint')"
											/>
											<span
												class="form-input__error | form-input-error"
												v-if="errors.length > 0 && errors[0]"
												v-html="errors[0]"
											/>
											<span
												class="form-input__success | form-input-success"
												v-if="!!couponSuccess"
												v-html="couponSuccess"
											/>
										</ValidationProvider>
									</div>
								</div>
								<div
									class="form-row"
									v-if="accountCoupons.length"
								>
									<div class="form-row__wrapper checkout-form__coupons">
										<span v-html="$t('checkout.couponUserTitle')"/>
										<FormInputSelect
											class="checkout-form__coupons-select"
											:options="accountCouponsEnhanced"
											@input="onCouponCodeSelect"
											:label="$t('checkout.couponUserLabel')"
											:disabled="formLocked"
										/>
									</div>
								</div>
							</div>

							<div class="checkout-form__block">
								<div class="accept">
									<FormInputCheckbox
										name="newsletter"
										value="newsletter"
										v-model="form.newsletter"
										immediate
										:disabled="formLocked"
									>
										<span v-html="$t('checkout.newsletterLabel')"/>
									</FormInputCheckbox>

									<ValidationProvider
										v-if="cartIncludesAlcohol"
										:rules="{ required: { allowFalse: false } }"
										name="checkAlcohol"
										tag="div"
										v-slot="{ invalid, errors, validated }"
										:customMessages="{ required: $t('checkout.errorAlcohol') }"
									>
										<FormInputCheckbox
											name="alcohol"
											value="checkAlcohol"
											v-model="form.checkAlcohol"
											:error="errors.length > 0"
											:error-message="errors[0]"
											required
											immediate
											:disabled="formLocked"
										>
											<span v-html="$t('checkout.alcoholLabel')"/>
										</FormInputCheckbox>
									</ValidationProvider>

									<ValidationProvider
										:rules="{ required: { allowFalse: false } }"
										name="checkAgb"
										tag="div"
										v-slot="{ invalid, errors, validated }"
										:customMessages="{ required: $t('checkout.errorTerms') }"
									>
										<FormInputCheckbox
											name="terms"
											value="checkAgb"
											v-model="form.checkAgb"
											:error="errors.length > 0"
											:error-message="errors[0]"
											required
											immediate
											:disabled="formLocked"
										>
											<span v-html="$t('checkout.termsLabel')"/>
										</FormInputCheckbox>
									</ValidationProvider>
									<!-- Currently not supported
									<fieldset class="checkbox">
										<span class="checkbox-area">
											<input
												type="checkbox"
												name=""
												id="account"
												class="checkbox"
												value=""
											/>
											<label id="" for="account"
												>Ich möchte einen Account erstellen und meine Daten
												speichern.</label
											>
										</span>
									</fieldset>
									-->

									<!-- DEBUG OUT -->
									<!--
									<span>
										{{Object.entries(errors).map(([k, v]) => `${k}: ${v.join(",")}`).join(", ")}}
									</span>
									<span v-show="formLocked" style="color: red">FORM LOCKED</span>
									-->
									<!-- END DEBUG OUT-->
									<Button
										type="button"
										level="primary"
										:text="$t('checkout.orderButton')"
										:disabled="formLocked"
										@click="onSubmit"
									/>

									<div>
										<Button
											@click.prevent="backToShop"
											class="checkout-form__back-to-shop checkout-form__back-to-shop--bottom"
											level="tertiary"
											type="button"
											:text="$t('checkout.continueShopping')"
											arrow
											reverse
											:disabled="formLocked"
										/>
									</div>
								</div>
							</div>
						</ValidationObserver>
					</template>
				</template>


			</div>
		</div>
		<div class="checkout-form__cart-placeholder insicht insicht--fade-in insicht--fast">
			<OverlaySidebarCart
				ref="sidebarCart"
				:is-editable="false"
				:is-inline="true"
			/>
		</div>
	</div>
</template>

<script>
import Vue from "vue"
import { mapGetters } from "vuex"
import { ValidationProvider, ValidationObserver } from "vee-validate"
import FormInput from "@/components/FormInput"
import FormInputZip from "@/components/FormInputZip"
import FormInputTextarea from "@/components/FormInputTextarea"
import FormInputPayment from "@/components/FormInputPayment"
import FormInputCheckbox from "@/components/FormInputCheckbox"
import FormInputRadio from "@/components/FormInputRadio"
import FormInputSelect from "@/components/FormInputSelectCustom"
import FormInputRange from "@/components/FormInputRange"
import FormInputDate from "@/components/FormInputDate"
import TimePicker from "@/components/TimePicker"
import veevalidate from "@/helpers/veevalidate"
import { dayjs, isSameDay, dayjsLocal } from "@/helpers/dayjs"
import OverlaySidebarCart from '@/pages/OverlaySidebarCart'
import CheckoutCartButton from '@/components/CheckoutCartButton'
import GeneralMessage from '@/components/GeneralMessage'
import Button from '@/components/Button'
import scrrroll from 'scrrroll'
import {eventhub, EVENT_ACCOUNT_ISLOGGEDIN} from '@/store/eventhub'
import checkoutPrefill from '@/helpers/checkoutPrefill'
import htmlComponent from "htmlcomponent"
import { formatDate } from '@/utils/formatDate'
import FormInputAutocomplete from "@/components/FormInputAutocomplete.vue"

export default {
	components: {
		ValidationProvider,
		ValidationObserver,
		FormInput,
		FormInputAutocomplete,
		FormInputZip,
		FormInputTextarea,
		FormInputPayment,
		FormInputCheckbox,
		FormInputRadio,
		FormInputSelect,
		FormInputRange,
		FormInputDate,
		TimePicker,
		OverlaySidebarCart,
		CheckoutCartButton,
		GeneralMessage,
		Button,
	},
	data() {
		return {
			countryOptions: (this.themeValue('THEME_COUNTRY_LIST') || []).map(country => ({
				value: country,
				label: this.$t(`forms.countries.${country}`)
			})),
			genders: [
				{
					value: 'f',
					label: this.$t('forms.genders.f'),
				},
				{
					value: 'm',
					label: this.$t('forms.genders.m'),
				},
				{
					value: 'd',
					label: this.$t('forms.genders.d'),
				},
			],
			form: {
				email: "",
				shippingAddress: {
					gender: "",
					firstName: "",
					lastName: "",
					countryCode: this.themeValue('THEME_COUNTRY'),
					street: "",
					streetNumber: "",
					details: "",
					city: "",
					postcode: "",
					provinceName: "",
					company: "",
					phoneNumber: "",
				},
				billingAddressIdentical: true,
				billingAddress: {
					gender: "",
					firstName: "",
					lastName: "",
					countryCode: this.themeValue('THEME_COUNTRY'),
					street: "",
					streetNumber: "",
					details: "",
					city: "",
					postcode: "",
					provinceName: "",
					company: "",
					phoneNumber: ""
				},
				paymentCode: '',
				tip: 0,
				shippingId: 0,
				voucherCode: "",
				notes: '',
				checkAgb: false,
				checkAccount: false,
				checkAlcohol: false,
				dropoffType: 1,
				newsletter: true,
				birthday: '',
				chopsticks: false,
			},
			couponCode: "",
			couponSuccess: '',
			debug: false,
			tipAllowedMethods: [
				'datatrans',
				'boncard',
				'boncard-giftcard',
				'postfinance',
				'twint',
			],
			billingAddressMethods: [
				'invoice',
			],
			birthdayMethods: [
				'byjuno',
			],
			addressSet: false,
			deliverySet: false,
			optionsSet: false,
			paymentSet: false,
			selectedDate: '',
			selectedTime: '',
			addressInputTimeout: null,
			addressSuggestions: []
		};
	},
	computed: {
		...mapGetters("checkout", [
			"checkoutPayments",
			"checkoutFormData",
			"checkoutBasketZip",
			"checkoutDeliveryDates",
			"checkoutDeliveryMethods",
			"checkoutDeliveryMethod",
		]),
		...mapGetters("notification", [
			"notifyMessages"
		]),
		...mapGetters("cart", [
			"discountCode",
			"subtotal",
			"deliveryTime",
			"cartDeliveryTime",
			"cartDeliveryTimeslot",
			"cartIncludesAlcohol",
			"shippingMethod",
			"cartOrderTimePassed",
			"cartOrderNow",
			"cartTip",
			"cartTipEnabled",
			"cartTipDefault",
			"shopLocation",
			"cartZip",
		]),
		...mapGetters("account", [
			"isLoggedIn",
			"accountLastAddress",
			"accountUserDetail",
			"accountCoupons",
		]),
		...mapGetters('loader', [
			'isLoading',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
		formLocked() {
			return this.loading;
		},
		deliveryTimeEnhanced() {
			return this.cartDeliveryTime
				? `${this.cartDeliveryTime.locale(this.language.split('-')[0]).format('ddd DD.MM.YYYY')} | ${this.cartDeliveryTimeslot || this.cartDeliveryTime.format('LT')}`
				: '';
		},
		accountCouponsEnhanced() {
			return this.accountCoupons
				.map(coupon => {
					return {
						value: coupon.code,
						label: coupon.name,
						disabled: coupon.code == this.discountCode,
					};
				});
		},
		showTipInput() {
			return this.cartTipEnabled && this.tipAllowedMethods.includes(this.form.paymentCode) && this.shippingMethod && this.shippingMethod.code == "delivery";
		},
		showBillingAddress() {
			return this.billingAddressMethods.includes(this.form.paymentCode);
		},
		showBirthday() {
			return this.birthdayMethods.includes(this.form.paymentCode);
		},
		showPostdeliveryHint() {
			return !this.isLoggedIn && this.subtotal >= 8900 && this.subtotal <= 10900;
		},
		addressTitle() {
			return this.isDelivery ? this.$t('checkout.shippingAddress') : this.$t('checkout.address');
		},
		timeTitle() {
			return this.isDelivery ? this.$t('checkout.timeTitleDelivery') : this.$t('checkout.timeTitleTakeaway');
		},
		timeEditLabel() {
			return this.isDelivery ? this.$t('checkout.changeDeliveryTime') : this.$t('checkout.changeTakeawayTime');
		},
		tip: {
			get() {
				return this.cartTip
			},
			set(value) {
				this.$store.dispatch('cart/adjustTip', { value })
			}
		},
		isTakeaway() {
			return this.shippingMethod?.code == 'takeaway';
		},
		isDelivery() {
			return !this.isTakeaway;
		},
		shopLocationAddress() {
			const locationAddress = this.shopLocation?.location?.address;
			let address = [];

			if (locationAddress) {
				let { street, streetNumber, zip, city } = locationAddress;
				if (street || streetNumber) {
					const sn = [
						street || '',
						streetNumber || ''
					];

					if (this.themeValue('THEME_REVERSE_STREET_NUMBER')) {
						sn.reverse();
					}

					address.push(sn.join(' '));
				}
				if (zip && city) {
					address.push(`${zip} ${city}`);
				}
			}

			return address.join('<br/>');
		},
		formattedShippingAddress() {
			const streetNumber = [
				this.form.shippingAddress.street || '',
				this.form.shippingAddress.streetNumber || ''
			];

			if (this.themeValue('THEME_REVERSE_STREET_NUMBER')) {
				streetNumber.reverse();
			}

			let address = [
				this.form.shippingAddress.company,
				[this.form.shippingAddress.firstName, this.form.shippingAddress.lastName].join(' '),
				streetNumber.join(' '),
				this.form.shippingAddress.details ? `<small>${this.form.shippingAddress.details}</small>` : '',
				[this.form.shippingAddress.postcode, this.form.shippingAddress.city].join(' '),
				this.countryOptions.find(country => country.value === this.form.shippingAddress.countryCode)?.label || '',
				this.form.shippingAddress.phoneNumber,
				this.form.email,
			]
			return address.filter(line => line.trim()).join('<br>')
		},
		deliveryDates() {
			let dates = this.checkoutDeliveryDates;

			if (this.deliveryMethods.length && this.deliveryMethod) {
				dates = this.checkoutDeliveryMethod.deliveryDates;
			}

			return dates.map(date => date.deliveryDate)
		},
		deliveryMinDate() {
			return this.deliveryDates.at(0)
		},
		deliveryMaxDate() {
			return this.deliveryDates.at(-1)
		},
		deliveryTimes() {
			let dates = this.checkoutDeliveryDates;

			if (this.deliveryMethods.length && this.deliveryMethod) {
				dates = this.checkoutDeliveryMethod.deliveryDates;
			}

			const date = dates.find(d => d.deliveryDate === this.selectedDate);

			if (date) {
				return date.timeslots.map(slot => {
					let label = slot.slot;
					let prices = ''

					if (slot.price != null && slot.price > 0) {
						prices = this.$options.filters.madpricecent(slot.price)
					}

					if (slot.surcharge != null && slot.surcharge > 0) {
						if (prices) {
							prices += ' '
						}
						prices += `+ ${this.$options.filters.madpricecent(slot.surcharge)}`
					}

					if (prices) {
						label += ` (${prices})`
					}

					return {
						value: slot.slot,
						label,
						productCode: slot.productCode
					}
				})
			}

			return []
		},
		deliveryMethod: {
			get() {
				return this.checkoutDeliveryMethod?.code
			},
			set(code) {
				this.$store.dispatch('checkout/setDeliveryMethod', { code })
			}
		},
		deliveryMethods() {
			return this.checkoutDeliveryMethods
			// .reduce((a, m) => [...a, m, m], [])
			.map(method => ({
				value: method.code,
				label: this.$t(`checkout.delivery.method.${method.code}`)
			}))
		},
	},
	watch: {
		couponCode(val) {
			if (val) {
				this.couponSuccess = '';
			}
		},
		async deliveryDates(val) {
			await this.$nextTick()
			if (!val.find(d => d === this.selectedDate)) {
				this.selectedDate = this.deliveryMinDate;
			}
		},
		async selectedDate(val) {
			await this.$nextTick()
			if (!this.deliveryTimes.find(t => t.value === this.selectedTime)) {
				this.selectedTime = this.deliveryTimes.at(0)?.value
			}
		},
		async deliveryMethod() {
			await this.$nextTick()
			if (!this.deliveryTimes.find(t => t.value === this.selectedTime)) {
				this.selectedTime = this.deliveryTimes.at(0)?.value
			}
			this.onDeliveryTime(false)
		},
	},
	methods: {
		onAddressInput() {
			if (this.hasFeature('FEATURE_ADDRESS_AUTOCOMPLETE')) {
				clearTimeout(this.addressInputTimeout)
				this.addressInputTimeout = setTimeout(async () => {
					try {
						const response = await this.$store.dispatch('checkout/addressAutocomplete', {
							street: this.form.shippingAddress.street,
							streetNumber: this.form.shippingAddress.streetNumber,
							postcode: this.form.shippingAddress.postcode
						})
						this.addressSuggestions = response?.suggestions ?? []
					} catch {
						this.addressSuggestions = []
					}
				}, 300);
			}
		},
		onAddressSuggestion(suggestion) {
			if (this.hasFeature('FEATURE_ADDRESS_AUTOCOMPLETE')) {
				this.form.shippingAddress.street = suggestion?.street || this.form.shippingAddress.street
				this.form.shippingAddress.streetNumber = suggestion?.streetNumber || this.form.shippingAddress.streetNumber
				this.form.shippingAddress.postcode = suggestion?.postcode || this.form.shippingAddress.postcode
				this.form.shippingAddress.city = suggestion?.city || this.form.shippingAddress.city

				if (!suggestion?.streetNumber) {
					const input = this.$refs.shippingStreetNumber.$el.querySelector('input')
					input.focus()
				}
			}
		},
		scrollToForm() {
			scrrroll.to(this.$refs.shippingAddressForm?.$el || this.$refs.deliveryTimeForm?.$el || this.$refs.form?.$el);
		},
		openLogin() {
			htmlComponent.send('overlay:open', {type: 'login'});
		},
		openRegister() {
			htmlComponent.send('overlay:open', {type: 'login', tab: 'register'});
		},
		joinErrorMessage(errors) {
			let messages = [];
			Object.values(errors).filter(value => !!value.join('').trim()).forEach(m => {
				const message = m.join('').trim();
				if (!messages.includes(message)) {
					messages.push(message);
				}
			});
			return messages.join(' ');
		},
		backToShop() {
			this.$store.dispatch('redirect', {name: 'shop'})
		},

		onShippingAddressEdit() {
			this.addressSet = false;
		},

		async onShippingAddress() {
			this.loading = true
			const isValid = await this.$refs.shippingAddressForm.validate()
			if (isValid) {
				this.$store.dispatch('notification/clearMessages')
				try {
					// If billing address has been entered, but is not necessary anymore, overwrite it with the shipping address
					if ((this.showBillingAddress && this.form.billingAddressIdentical) || !this.showBillingAddress) {
						Object.keys(this.form.billingAddress).forEach(key => {
							Vue.set(this.form.billingAddress, key, this.form.shippingAddress[key])
						})
					}

					await this.$store.dispatch('setShippingAddress', {
						form: {
							...this.form
						}
					})
					this.addressSet = true;
					this.deliverySet = false;
					// this.loading = false;
					await this.$nextTick();
					await scrrroll.to(this.$refs.deliveryTimeForm.$el);
				} catch (err) {
					const errorData = err.response.data
					let notificationMessage = errorData
					if (typeof notificationMessage === "object") {
						let errors = notificationMessage.errors
						if (errors) {
							//handle errors
							notificationMessage = Object.values(errors)
								.map(v => v[0])
								.join(", ")
						} else {
							notificationMessage = notificationMessage.message
						}
					}

					if (errorData?.changes?.shippingAddress) {
						Object.entries(errorData?.changes?.shippingAddress).forEach(([key, value]) => {
							if (Object.hasOwn(this.form.shippingAddress, key)) {
								Vue.set(this.form.shippingAddress, key, value)
							}
						})
					}

					this.$store.dispatch('notification/addMessage', {
						message: {
							message: notificationMessage || this.$t('forms.errors.generic'),
							type: "error"
						}
					})
					await this.$nextTick()
					let visibleNotification = this.$refs.notificationMessages.find(v => v.visible)
					if (visibleNotification) {
						scrrroll.to(visibleNotification.$el)
					}
					// this.loading = false
				}
			} else {
				let notifications = Object
					.entries(this.$refs.shippingAddressForm.errors)
					.filter(([key, val]) => val != null && val.length > 0)
					.filter((v, i) => i == 0)
					.map(([key, val]) => key)

				let validator = this.$refs.shippingAddressForm.refs[notifications[0]] || this.$refs[notifications[0]]
				if (validator) {
					scrrroll.to(validator.$el)
				}
			}
			this.loading = false
		},

		onDeliveryTimeEdit() {
			this.deliverySet = false;
		},

		async onDeliveryTime(set = true) {
			this.loading = true
			try {
				const isValid = await this.$refs.deliveryTimeForm.validate()
				if (isValid) {
					this.$store.dispatch('notification/clearMessages')
					try {
						const selectedDeliveryTime = this.deliveryTimes.find(t => t.value === this.selectedTime)

						await this.$store.dispatch('updateDeliverTime', {
							orderNow: false,
							date: this.selectedDate,
							time: this.selectedTime.split(/\s?-\s?/)[0],
							timeslot: this.selectedTime,
							method: this.deliveryMethod,
							productCode: selectedDeliveryTime?.productCode,
							confirmChange: true
						})

						set && (this.deliverySet = true)
						// this.loading = false;
						await this.$nextTick()
						await scrrroll.to(this.$refs.form.$el)
					} catch (err) {
						let notificationMessage = err.response.data
						if (typeof notificationMessage === "object") {
							let errors = notificationMessage.errors
							if (errors) {
								//handle errors
								notificationMessage = Object.values(errors)
									.map(v => v[0])
									.join(", ")
							} else {
								notificationMessage = notificationMessage.message
							}
						}
						this.$store.dispatch('notification/addMessage', {
							message: {
								message: notificationMessage || this.$t('forms.errors.generic'),
								type: "error"
							}
						})
						await this.$nextTick()
						let visibleNotification = this.$refs.notificationMessages.find(v => v.visible)
						if (visibleNotification) {
							scrrroll.to(visibleNotification.$el)
						}
						// this.loading = false
					}
				} else {
					let notifications = Object
						.entries(this.$refs.deliveryTimeForm.errors)
						.filter(([key, val]) => val != null && val.length > 0)
						.filter((v, i) => i == 0)
						.map(([key, val]) => key)

					let validator = this.$refs.deliveryTimeForm.refs[notifications[0]] || this.$refs[notifications[0]]
					if (validator) {
						scrrroll.to(validator.$el)
					}
				}
			} catch {}
			this.loading = false
		},


		async onSubmit() {
			this.loading = true
			const isValid = await this.$refs.form.validate()
			if (isValid) {
				this.$store.dispatch('notification/clearMessages')
				try {
					// reset tip before submit if its not visible
					if (this.showTipInput && this.tip > 0) {
						this.form.tip = this.tip
					} else {
						this.form.tip = 0
					}

					await this.$store.dispatch('submitOrder', {
						form: {
							...this.form
						},
						deliverTime: this.cartDeliveryTime
					})
				} catch(err) {
					// console.error("PageCheckout::submit", err)

					//TODO: we have to handle a lot of different error formats... crap...
					let notificationMessage = err.response.data
					if (typeof notificationMessage === "object") {
						let errors = notificationMessage.errors
						if (errors) {
							//handle errors
							notificationMessage = Object.values(errors)
								.map(v => v[0])
								.join(", ")
						} else {
							notificationMessage = notificationMessage.message
						}
					}
					this.$store.dispatch('notification/addMessage', {
						message: {
							message: notificationMessage || this.$t('forms.errors.generic'),
							type: "error"
						}
					})
					await this.$nextTick()
					let visibleNotification = this.$refs.notificationMessages.find(v => v.visible)
					if (visibleNotification) {
						scrrroll.to(visibleNotification.$el)
					}
					// this.loading = false
				}
			} else {
				let notifications = Object
					.entries(this.$refs.form.errors)
					.filter(([key, val]) => val != null && val.length > 0)
					.filter((v, i) => i == 0)
					.map(([key, val]) => key)

				let validator = this.$refs.form.refs[notifications[0]] || this.$refs[notifications[0]]
				if (validator) {
					scrrroll.to(validator.$el)
				}
			}
			this.loading = false
		},
		onCouponCodeSelect(value) {
			this.couponCode = value;
		},
		onPaymentChanged(value) {
			if (!this.showTipInput) {
				this.tip = this.cartTipDefault
			}
		},
		async addCoupon() {
			this.loading = true
			this.couponSuccess = '';
			const validateResult = await this.$refs.couponCode.validate()
			if (this.couponCode && validateResult.valid) {
				let couponResult = await this.$store.dispatch('cart/addCoupon',{coupon: this.couponCode, email: this.form.email})
					.catch(err => Promise.resolve({error: true, ...err}))
				//TODO: handle server error message here
				if (couponResult && couponResult.error == true) {
					this.$refs.couponCode.setErrors(couponResult.response.data.errors.coupon)
				} else {
					//update payments
					await this.$store.dispatch('checkout/payments')
					this.couponCode = '';
					this.couponSuccess = this.$t('checkout.successCoupon');
				}
			}
			this.loading = false
		},
		isDateDisabled(d) {
			const { date } = formatDate(d)
			return !this.deliveryDates.find(d => d === date)
		},
		onIsLoggedIn() {
			this.prefillFormdata()
		},
		prefillFormdata() {
			let formData = this.checkoutFormData

			// TODO: we outsorced this part, but we have to provide all access to this function
			checkoutPrefill({
				formData,
				accountUserDetail: this.accountUserDetail,
				accountLastAddress: this.accountLastAddress,
				cartZip: this.checkoutBasketZip,
				form: this.form,
				$store: this.$store
			})

		}
	},
	mounted() {
		eventhub.$on(EVENT_ACCOUNT_ISLOGGEDIN, this.onIsLoggedIn)
	},
	destroyed() {
		eventhub.$off(EVENT_ACCOUNT_ISLOGGEDIN, this.onIsLoggedIn)
	},
	async created() {
		this.loading = true
		await this.$store.dispatch('initializeCheckout')
		this.prefillFormdata()
		this.addressSet = this.cartZip && this.checkoutFormData.shippingAddress && this.shippingMethod && this.form.email

		if (this.addressSet) {
			await this.$store.dispatch('checkout/setShipping', { zip: this.cartZip })
		}

		this.deliverySet = this.cartDeliveryTime || this.cartDeliveryTimeslot;

		if (this.deliverySet) {
			const { date, time } = formatDate(this.cartDeliveryTime);
			this.selectedDate = date;
			this.selectedTime = this.cartDeliveryTimeslot || time;
		}

		this.loading = false
	}
}
</script>

<style></style>
